import React, { useState, useEffect, useRef } from 'react';
import { useAktiveModule } from '../hooks/useAktiveModule';
import DashboardOverview from '../components/DashboardOverview';
import PartnerList from '../components/PartnerList';
import ArtikelList from '../components/ArtikelList';
import LagerbestandList from '../components/LagerbestandList';
import LagerbewegungList from '../components/LagerbewegungList';
import BestellungList from '../components/BestellungList';
import FertigungsauftragList from '../components/FertigungsauftragList';
import QualitaetOverview from '../components/QualitaetOverview';
import AuftragList from '../components/AuftragList';
import DokumenteOverview from '../components/DokumenteOverview';
import WarnungenList from '../components/WarnungenList';
import MusterVorschlaege from '../components/MusterVorschlaege';
import KanbanBoard from '../components/KanbanBoard';
import ImportWizard from '../components/ImportWizard';
import StuecklistenImport from '../components/StuecklistenImport';
import KatalogeVerwaltung from '../components/KatalogeVerwaltung';
import AufgabenOverview from '../components/AufgabenOverview';
import EinsatzPlantafel from '../components/EinsatzPlantafel';
import ProjektList from '../components/ProjektList';
import ZeitbuchungList from '../components/ZeitbuchungList';
import MaterialentnahmeList from '../components/MaterialentnahmeList';
import VertragList from '../components/VertragList';
import ZahlungList from '../components/ZahlungList';
import RetoureList from '../components/RetoureList';
import SendungList from '../components/SendungList';
import WorkflowManager from '../components/WorkflowManager';
import RegieberichteOverview from '../components/RegieberichteOverview';
import { NAVIGIERE_AUFTRAG_EVENT, NavigiereAuftragDetail, setAuftragNavigationVerfuegbar } from '../lib/navigation';
import './ManagementPage.css';

type TabType = 'dashboard' | 'warnungen' | 'kunden' | 'artikel' | 'lager' | 'bewegungen' | 'bestellungen' | 'fertigung' | 'qualitaet' | 'auftraege' | 'aufgaben' | 'kanban' | 'dokumente' | 'vertraege' | 'zahlungen' | 'retouren' | 'sendungen' | 'muster' | 'import' | 'stuecklisten' | 'kataloge' | 'workflows' | 'projekte' | 'zeitbuchung' | 'materialentnahme' | 'einsaetze' | 'regieberichte';

interface Tab {
  id: TabType;
  label: string;
  rollen: string[]; // Welche Rollen diesen Tab sehen dürfen (leer = alle)
  modul?: string;   // Falls gesetzt: Tab nur sichtbar wenn dieses Modul aktiv ist
}

const ALL_TABS: Tab[] = [
  // Übersicht (immer sichtbar)
  { id: 'dashboard',    label: 'Dashboard',     rollen: [] },
  { id: 'warnungen',    label: 'Warnungen',     rollen: [] },
  // Stammdaten
  { id: 'kunden',       label: 'Kontakte',       rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Lagerleiter'], modul: 'partner' },
  { id: 'artikel',      label: 'Artikel',        rollen: [], modul: 'artikel' },
  // Lager & Einkauf
  { id: 'lager',        label: 'Lager',          rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Produktionsleiter'], modul: 'lager' },
  { id: 'bewegungen',   label: 'Bewegungen',     rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter'], modul: 'lager' },
  { id: 'bestellungen', label: 'Bestellungen',   rollen: ['Admin', 'Geschäftsführer', 'Einkäufer'], modul: 'bestellung' },
  // Produktion & Qualität
  { id: 'fertigung',    label: 'Fertigung',      rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter'], modul: 'fertigungsauftrag' },
  { id: 'qualitaet',    label: 'Qualität',       rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter', 'Lagerleiter'], modul: 'qualitaet' },
  // Verkauf & Finanzen
  { id: 'auftraege',    label: 'Aufträge',       rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Einkäufer', 'Lagerleiter'], modul: 'auftrag' },
  { id: 'aufgaben',     label: 'Aufgaben',        rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter'] },
  { id: 'kanban',       label: 'Kanban',          rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter'], modul: 'auftrag' },
  { id: 'dokumente',    label: 'Dokumente',      rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Buchhaltung'] },
  { id: 'vertraege',   label: 'Verträge',       rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Verkäufer', 'Buchhaltung'] },
  { id: 'zahlungen',   label: 'Zahlungen',      rollen: ['Admin', 'Geschäftsführer', 'Buchhaltung'], modul: 'zahlung' },
  { id: 'retouren',    label: 'Retouren',       rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Lagerleiter', 'Buchhaltung'], modul: 'retoure' },
  { id: 'sendungen',   label: 'Sendungen',      rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Verkäufer'], modul: 'sendung' },
  // Admin (Querschnitt — immer sichtbar für berechtigte Rollen)
  { id: 'muster',       label: 'Systemvorschläge', rollen: ['Admin'] },
  { id: 'import',       label: 'Import',             rollen: ['Admin', 'Einkäufer'] },
  { id: 'stuecklisten', label: 'Stücklisten',        rollen: ['Admin', 'Einkäufer'] },
  { id: 'kataloge',     label: 'Kataloge',           rollen: ['Admin', 'Einkäufer'] },
  { id: 'workflows',    label: 'Workflows',           rollen: ['Admin'] },
  // Handwerk / Projekt (module-gated)
  { id: 'projekte',     label: 'Projekte',           rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter'], modul: 'projekt' },
  { id: 'zeitbuchung',  label: 'Zeitbuchung',        rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter'], modul: 'zeitbuchung' },
  { id: 'materialentnahme', label: 'Materialentnahme', rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter'], modul: 'materialentnahme' },
  { id: 'einsaetze', label: 'Einsatzplanung', rollen: ['Admin', 'Geschäftsführer', 'Projektleiter', 'Projektmitarbeiter'], modul: 'einsatz' },
  { id: 'regieberichte', label: 'Regieberichte', rollen: ['Admin', 'Geschäftsführer', 'Projektleiter', 'Projektmitarbeiter', 'Buchhaltung'], modul: 'einsatz' },
];

interface AuthUser {
  name: string;
  email: string;
  rolle: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  firmenname: string | null;
  primaerfarbe: string | null;
}

interface ManagementPageProps {
  userRolle?: string;
  authToken?: string;
  authUser?: AuthUser | null;
  initialTab?: string;
  onInitialTabConsumed?: () => void;
}

const ManagementPage: React.FC<ManagementPageProps> = ({ userRolle, authToken, authUser, initialTab, onInitialTabConsumed }) => {
  const aktiveModule = useAktiveModule();

  // Tabs filtern basierend auf Rolle UND aktiven Modulen
  const sichtbareTabs = ALL_TABS.filter(tab => {
    const rolleOk = tab.rollen.length === 0 ||
      !userRolle ||
      (userRolle !== 'SuperAdmin' && userRolle === 'Admin') ||
      tab.rollen.includes(userRolle);
    // Wenn Modul-Set noch leer (loading), zeige alle Tabs (kein Flicker)
    const modulOk = !tab.modul || aktiveModule.size === 0 || aktiveModule.has(tab.modul);
    return rolleOk && modulOk;
  });

  const [activeTab, setActiveTab] = useState<TabType>(
    sichtbareTabs.length > 0 ? sichtbareTabs[0].id : 'artikel'
  );

  const [pendingAuftragId, setPendingAuftragId] = useState<number | null>(null);

  // Rollen wie Projektleiter/Projektmitarbeiter/Buchhaltung sehen den
  // Regieberichte-Tab, aber NICHT den Aufträge-Tab — für sie darf
  // navigiereZuAuftrag() weder den Tab wechseln (currentTab fiele sonst auf
  // den ersten sichtbaren Tab zurück, meist Dashboard) noch die Chips
  // anzeigen, die dorthin verlinken. Per Ref statt nur als Closure-Wert im
  // Effekt, damit der einmalig registrierte Listener unten trotzdem den
  // jeweils aktuellen Stand sieht, statt den vom Mount einzufrieren.
  const auftragTabSichtbar = sichtbareTabs.some(t => t.id === 'auftraege');
  const auftragTabSichtbarRef = useRef(auftragTabSichtbar);
  auftragTabSichtbarRef.current = auftragTabSichtbar;

  useEffect(() => {
    setAuftragNavigationVerfuegbar(auftragTabSichtbar);
  }, [auftragTabSichtbar]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<NavigiereAuftragDetail>).detail;
      if (!detail) return;
      if (!auftragTabSichtbarRef.current) return;
      setPendingAuftragId(detail.auftragId);
      setActiveTab('auftraege');
    };
    window.addEventListener(NAVIGIERE_AUFTRAG_EVENT, handler);
    return () => window.removeEventListener(NAVIGIERE_AUFTRAG_EVENT, handler);
  }, []);

  // Wenn initialTab gesetzt (z.B. von Command Palette), wechsle zu diesem Tab
  useEffect(() => {
    if (initialTab && sichtbareTabs.some(t => t.id === initialTab)) {
      setActiveTab(initialTab as TabType);
      onInitialTabConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // Wenn aktiver Tab nicht mehr sichtbar → ersten sichtbaren Tab wählen
  const currentTab = sichtbareTabs.find(t => t.id === activeTab)
    ? activeTab
    : (sichtbareTabs[0]?.id ?? 'artikel');

  return (
    <div className="management-page">
      <div className="tab-navigation">
        {sichtbareTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {currentTab === 'dashboard'    && (
          <DashboardOverview
            authToken={authToken || ''}
            onNavigate={(tab) => setActiveTab(tab as TabType)}
          />
        )}
        {currentTab === 'warnungen'    && <WarnungenList authToken={authToken || ''} />}
        {currentTab === 'kunden'       && <PartnerList />}
        {currentTab === 'artikel'      && <ArtikelList />}
        {currentTab === 'lager'        && <LagerbestandList />}
        {currentTab === 'bewegungen'   && <LagerbewegungList />}
        {currentTab === 'bestellungen' && <BestellungList />}
        {currentTab === 'fertigung'    && <FertigungsauftragList />}
        {currentTab === 'qualitaet'    && <QualitaetOverview />}
        {currentTab === 'auftraege'    && (
          <AuftragList
            initialAuftragId={pendingAuftragId}
            onInitialAuftragConsumed={() => setPendingAuftragId(null)}
          />
        )}
        {currentTab === 'aufgaben'     && <AufgabenOverview authToken={authToken || ''} />}
        {currentTab === 'dokumente'    && <DokumenteOverview />}
        {currentTab === 'vertraege'    && <VertragList />}
        {currentTab === 'zahlungen'    && <ZahlungList />}
        {currentTab === 'retouren'     && <RetoureList />}
        {currentTab === 'sendungen'    && <SendungList />}
        {currentTab === 'muster'          && <MusterVorschlaege authToken={authToken || ''} />}
        {currentTab === 'workflows'       && <WorkflowManager authToken={authToken || ''} />}
        {currentTab === 'kanban'          && <KanbanBoard authToken={authToken || ''} />}
        {currentTab === 'import'          && <ImportWizard authToken={authToken || ''} />}
        {currentTab === 'stuecklisten'    && <StuecklistenImport authToken={authToken || ''} />}
        {currentTab === 'kataloge'        && <KatalogeVerwaltung authToken={authToken || ''} />}

        {currentTab === 'projekte'       && <ProjektList authToken={authToken || ''} userRolle={userRolle} />}
        {currentTab === 'zeitbuchung'    && <ZeitbuchungList />}
        {currentTab === 'materialentnahme' && <MaterialentnahmeList />}
        {currentTab === 'einsaetze' && <EinsatzPlantafel userRolle={userRolle} />}
        {currentTab === 'regieberichte' && <RegieberichteOverview authToken={authToken || ''} userRolle={userRolle} />}
      </div>
    </div>
  );
};

export default ManagementPage;
